import { Search, RotateCcw } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { PRESETS, presetRange, makeFilters, STATUS_META } from "@/lib/format";

const Pick = ({ id, label, value, onChange, options, width = "w-44" }) => (
  <div className="flex flex-col gap-1">
    <span className="label-xs">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`${width} h-9 bg-white`} data-testid={`filter-${id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`filter-${id}-option-${o.value}`}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const FilterBar = ({ filters, setFilters, show = {}, defaultPreset = "12m" }) => {
  const { meta } = useAuth();
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));
  const stores = meta.stores.filter((s) => filters.region === "all" || s.region === filters.region);
  const concs = meta.concessions.filter((c) => filters.store === "all" || c.store_code === filters.store);
  const all = (label) => ({ value: "all", label });

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-white/70 p-4 backdrop-blur" data-testid="filter-bar">
      <Pick id="date-preset" label="Date range" value={filters.preset} options={PRESETS}
        onChange={(v) => set({ preset: v, ...(v === "custom" ? {} : presetRange(v)) })} />
      {filters.preset === "custom" && (<>
        <div className="flex flex-col gap-1"><span className="label-xs">From</span>
          <Input type="date" className="h-9 w-40 bg-white" value={filters.date_from} onChange={(e) => set({ date_from: e.target.value })} data-testid="filter-date-from" /></div>
        <div className="flex flex-col gap-1"><span className="label-xs">To</span>
          <Input type="date" className="h-9 w-40 bg-white" value={filters.date_to} onChange={(e) => set({ date_to: e.target.value })} data-testid="filter-date-to" /></div>
      </>)}
      {meta.regions.length > 1 && (
        <Pick id="region" label="Region" value={filters.region} onChange={(v) => set({ region: v, store: "all", concession: "all" })}
          options={[all("All regions"), ...meta.regions.map((r) => ({ value: r, label: r }))]} />
      )}
      {meta.stores.length > 1 && (
        <Pick id="store" label="Store" width="w-56" value={filters.store} onChange={(v) => set({ store: v, concession: "all" })}
          options={[all("All stores"), ...stores.map((s) => ({ value: s.code, label: s.name }))]} />
      )}
      {meta.concessions.length > 1 && concs.length > 0 && (
        <Pick id="concession" label="Concession" width="w-56" value={filters.concession} onChange={(v) => set({ concession: v })}
          options={[all("All (store + concessions)"), { value: "none", label: "Store only (no concession)" }, ...concs.map((c) => ({ value: c.code, label: c.name }))]} />
      )}
      {show.status && (
        <Pick id="status" label="Status" value={filters.status} onChange={(v) => set({ status: v })}
          options={[all("All statuses"), ...Object.entries(STATUS_META).map(([k, v]) => ({ value: k, label: v.label }))]} />
      )}
      {show.category && (
        <Pick id="category" label="Category" value={filters.category} onChange={(v) => set({ category: v })}
          options={[all("All categories"), ...meta.categories.map((c) => ({ value: c, label: c }))]} />
      )}
      {show.source && (
        <Pick id="source" label="Source" width="w-36" value={filters.source} onChange={(v) => set({ source: v })}
          options={[all("All sources"), { value: "web_upload", label: "Web file" }, { value: "portal", label: "Portal" }]} />
      )}
      {show.search && (
        <div className="flex flex-1 flex-col gap-1" style={{ minWidth: 200 }}>
          <span className="label-xs">Search</span>
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input className="h-9 bg-white pl-9" placeholder="Order no, PO ref, SAP code…" value={filters.search}
              onChange={(e) => set({ search: e.target.value })} data-testid="filter-search" />
          </div>
        </div>
      )}
      <button onClick={() => setFilters(makeFilters(defaultPreset))} data-testid="filter-reset"
        className="flex h-9 items-center gap-1.5 rounded-md px-3 text-sm text-slate-500 transition-colors hover:bg-black/5 hover:text-slate-900">
        <RotateCcw className="h-3.5 w-3.5" /> Reset
      </button>
    </div>
  );
};
